const DEFAULT_ID_LENGTH = 10

export function toBoolean (value) {
  if (value === 1 || value === '1') return true
  if (value === true || value === 'true') return true
  if (value === 0 || value === '0') return false
  if (value === false || value === 'false') return false
  return null
}

export function isUUID (value) {
  if (typeof value !== 'string') return false
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(value)
}

export function formatNumber (value, decimals = 0, options = {}) {
  const parsedValue = Number(value)
  if (!Number.isFinite(parsedValue)) return value

  const parsedDecimals = Number.parseInt(decimals, 10)
  const safeDecimals = Number.isInteger(parsedDecimals) && parsedDecimals >= 0 ? parsedDecimals : 0
  const absValue = options?.absolute ? Math.abs(parsedValue) : parsedValue

  return absValue.toLocaleString('en-US', {
    minimumFractionDigits: safeDecimals,
    maximumFractionDigits: safeDecimals
  })
}

export function generateString (length = DEFAULT_ID_LENGTH) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const targetLength = Number.isInteger(length) && length > 0 ? length : DEFAULT_ID_LENGTH
  let out = ''

  for (let i = 0; i < targetLength; i++) {
    const index = Math.floor(Math.random() * chars.length)
    out += chars[index]
  }

  return out
}

export function setPageAsFullHeight (isFullHeight, { containerSelector = '#app-content' } = {}) {
  if (typeof document === 'undefined') return

  const container = document.querySelector(containerSelector)
  if (!container) return

  const classes = ['position-absolute', 'top-to-bottom', 'left-to-right', 'width-100', 'overflow-auto']
  classes.forEach(className => container.classList.toggle(className, Boolean(isFullHeight)))
}

function makeColumnDefsFromRows (rows, gridColumns = {}) {
  if (!rows.length) return []

  const firstRow = rows[0] || {}
  const keys = Object.keys(firstRow)

  return keys
    .filter((field) => gridColumns[field]?.show !== false)
    .map((field) => ({
      field,
      ...(gridColumns[field] || {})
    }))
}

async function resolveRows (sourceUrl, data) {
  if (Array.isArray(data)) return data
  if (!sourceUrl) return []

  const response = await fetch(sourceUrl)
  const body = await response.json()

  if (Array.isArray(body)) return body
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body?.rows)) return body.rows

  const firstArrayProp = Object.values(body || {}).find((value) => Array.isArray(value))
  return Array.isArray(firstArrayProp) ? firstArrayProp : []
}

export class AutoAgGrid {
  constructor ({ containerSelector, sourceUrl, data, gridParams = {} } = {}) {
    this.containerSelector = containerSelector
    this.sourceUrl = sourceUrl
    this.data = data
    this.gridParams = gridParams

    this.gridApi = null
    this.ready = this.init()
  }

  async init () {
    if (typeof document === 'undefined') return null

    const containerElement = document.querySelector(this.containerSelector)
    if (!containerElement) return null

    const rows = await resolveRows(this.sourceUrl, this.data)
    const columns = this.gridParams?.columns || {}
    const columnDefs = makeColumnDefsFromRows(rows, columns)

    const { createGrid, themeQuartz } = await import('ag-grid-community')
    const gridOptions = {
      rowData: rows,
      columnDefs,
      defaultColDef: {
        filter: true,
        floatingFilter: true,
        sortable: true,
        resizable: true
      },
      autoSizeStrategy: {
        type: 'fitCellContents'
      },
      theme: themeQuartz,
      ...(this.gridParams || {})
    }

    delete gridOptions.columns
    this.gridApi = createGrid(containerElement, gridOptions)
    return this.gridApi
  }
}
